import { useSyncExternalStore } from "react";
import { BUNDLES } from "./bundles-data";
import { getToolById } from "./tools-data";

/**
 * "Continue with...": carries a finished result from one tool to the next one the person chooses.
 *
 * The file lives only in this browser tab's memory (never saved, never uploaded), is used once, and is
 * dropped as soon as the person opens any tool other than the one it was offered to, closes the tab, or
 * dismisses it.
 */
export interface Handoff { blob: Blob; fileName: string; fromToolId: string; toToolId: string; /** More files after the first, when a whole batch is carried. */ more?: { blob: Blob; fileName: string }[] }

/** Every file in a hand-off, in order. */
export function handoffFiles(h: Handoff): File[] {
  return [{ blob: h.blob, fileName: h.fileName }, ...(h.more ?? [])].map((f) => new File([f.blob], f.fileName, { type: f.blob.type }));
}

/**
 * Where a set of finished files can go next. Several PDFs can be merged; several pictures can become one PDF.
 * Mixed or other file types have no next step here.
 */
export function multiTargets(fileNames: string[]): { toolId: string; name: string }[] {
  if (fileNames.length < 2) return [];
  const ext = (n: string) => n.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? "";
  const exts = fileNames.map(ext);
  if (exts.every((e) => e === "pdf")) return [{ toolId: "merge-pdf", name: "Merge PDF" }];
  if (exts.every((e) => ["jpg", "jpeg", "png", "webp"].includes(e))) return [{ toolId: "image-to-pdf", name: "Image to PDF" }];
  return [];
}

let current: Handoff | null = null;
const listeners = new Set<() => void>();
const emit = () => Array.from(listeners).forEach((l) => l());

export function setHandoff(h: Handoff) { current = h; emit(); }
export function clearHandoff() { if (current) { current = null; emit(); } }
export function peekHandoff(toToolId: string): Handoff | null { return current && current.toToolId === toToolId ? current : null; }
/** Called when a tool page opens: a result offered to some other tool is dropped. */
export function dropHandoffUnless(toToolId: string) { if (current && current.toToolId !== toToolId) clearHandoff(); }

export function useHandoffFor(toToolId: string): Handoff | null {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => peekHandoff(toToolId),
    () => null,
  );
}

/** Natural next steps for tools that are not (or not only) inside a ready-made bundle. */
export const EXTRA_NEXT_STEPS: Record<string, string[]> = {
  "volume-booster": ["audio-normalizer"],
  "audio-converter": ["audio-trimmer"],
  "unlock-pdf": ["merge-pdf", "compress-pdf", "remove-pdf-pages"],
};

/** Tools that follow `toolId` in a ready-made bundle, most useful first, without repeats. */
export function continueWith(toolId: string, max = 3): { toolId: string; name: string }[] {
  const seen = new Set<string>();
  const out: { toolId: string; name: string }[] = [];
  for (const next of EXTRA_NEXT_STEPS[toolId] ?? []) {
    const tool = getToolById(next);
    if (!tool || tool.hidden || seen.has(next)) continue;
    seen.add(next);
    out.push({ toolId: next, name: tool.name });
  }
  for (const b of BUNDLES) {
    const i = b.steps.findIndex((s) => s.toolId === toolId);
    if (i < 0 || i >= b.steps.length - 1) continue;
    const next = b.steps[i + 1].toolId;
    const tool = getToolById(next);
    if (!tool || tool.hidden || seen.has(next)) continue;
    seen.add(next);
    out.push({ toolId: next, name: tool.name });
    if (out.length >= max) break;
  }
  return out;
}
